import React, { useState, useRef, useEffect } from 'react';
import { generateSizes, isExternalImage } from '@/utils/imageOptimizer';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder?: React.ReactNode;
  width?: number;
  height?: number;
  priority?: boolean;
}

export const LazyImage: React.FC<LazyImageProps> = ({ 
  src, 
  alt, 
  className = '', 
  placeholder,
  width,
  height,
  priority = false
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority); // Priority images are always "in view"
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // Skip IntersectionObserver for priority images
    if (priority) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  // Use original URLs directly for now (proxy disabled temporarily)
  const optimizedSrc = src;

  // No srcset for now
  const srcSet = undefined;
  const sizes = undefined;

  return (
    <div ref={imgRef} className={className}>
      {isInView ? (
        <img
          src={optimizedSrc}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          width={width}
          height={height}
          className={`transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'} ${className}`}
          onLoad={() => setIsLoaded(true)}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          decoding="async"
        />
      ) : (
        placeholder || <div className={`bg-muted animate-pulse ${className}`} />
      )}
    </div>
  );
};